// biome-ignore-all lint/a11y/noNoninteractiveTabindex: Informational tooltip triggers must be keyboard focusable.
import type { ReactNode } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/admin/coaching/coaching-dashboard/shared/tooltip'
import type { TrayCard } from '../_lib/auto-place'
import { InlineSourceSwitcher } from '../shared/inline-source-switcher'
import { SourceMark } from '../shared/source-mark'
import { isAbsoluteUrl } from './custom-card-form'

const TYPE_COLOR: Record<TrayCard['type'], string> = {
  coding: 'var(--cd-amber)',
  sd: 'var(--cd-purple)',
  behavioral: 'var(--cd-blue)',
  interview: 'var(--cd-green)',
  other: 'var(--cd-gray)',
}

const CONVERTIBLE_TYPES = new Set<TrayCard['type']>([
  'coding',
  'sd',
  'behavioral',
])

/**
 * Static, non-interactive copy of a card for the drag overlay. It mirrors the
 * face of whichever card was picked up — the tray article (`source: 'tray'`)
 * or a scheduled card (`source: 'schedule'`) — so the card keeps its size and
 * shape the moment it lifts off, instead of collapsing into a title pill.
 */
export function DragCardFace(props: {
  card: TrayCard
  source: 'tray' | 'schedule'
}): ReactNode {
  const { card, source } = props
  const tray = source === 'tray'
  return (
    <article
      aria-hidden="true"
      className={
        tray
          ? 'rounded-lg border border-l-[3px] border-(--cd-border-2) bg-(--cd-inset) px-2.5 py-2 shadow-lg'
          : 'rounded-[7px] border border-(--cd-border-2) bg-(--cd-inset) px-2 py-1.5 shadow-lg'
      }
      style={{ borderLeftColor: TYPE_COLOR[card.type], borderLeftWidth: '3px' }}
    >
      {/* The overlay mirrors the resting card, source mark included, so the
          title does not reflow the instant the card lifts off. */}
      <div className="flex items-start gap-1">
        <SourceMark icon={card.sourceIcon} decorative />
        <div className="min-w-0 text-[11px] font-semibold text-(--cd-text)">
          {card.title}
        </div>
      </div>
      <div className="text-[9.5px] text-(--cd-text-faint)">{card.meta}</div>
      <CardParentage card={card} />
    </article>
  )
}

/**
 * Where a card came from, and where else its item lives (LG-907).
 *
 * Two always-visible lines. `↳ from …` states **parentage** — which
 * container's assign created this card — which is stored on the row and never
 * rewritten, so it keeps saying how the card came to exist however the
 * catalog is edited afterwards. `Also in …` states **membership**, which is
 * the catalog as it is now. They are different facts and are deliberately not
 * merged into one list: a coach reading "from Ch 19" needs to know that is
 * history, not a live link.
 *
 * Renders nothing when there is neither — which is every card added directly,
 * so no existing board gains a row of chrome.
 */
/** How many containers the card names before it starts counting. */
const OTHER_MEMBERSHIP_NAME_LIMIT = 3

export function CardParentage(props: {
  card: TrayCard
  className?: string
}): ReactNode {
  const { card, className } = props
  const parent = card.creatingParent ?? null
  const others = card.otherMemberships ?? []
  if (parent === null && others.length === 0) return null
  // Capped for the same reason the assign flash caps its own list: this is
  // permanently visible now rather than behind a hover, and an item that sits
  // in Blind 75, NeetCode 150, Grind 169 and two more puts five wrapped lines
  // of grey under a two-line title, on every card of the week.
  const shownOthers = others.slice(0, OTHER_MEMBERSHIP_NAME_LIMIT)
  const unshownOthers = others.length - shownOthers.length
  const alsoIn = `Also in ${shownOthers.map((entry) => entry.title).join(', ')}${
    unshownOthers === 0 ? '' : ` and ${unshownOthers} more`
  }`
  return (
    <div
      // Stacked, not a shared flex line: a day column is ~130px, and both
      // rows wrap, so each needs the full column width. Sharing one line gave
      // them about fifteen characters each at 9px.
      className={`mt-0.5 flex min-w-0 flex-col items-start gap-0.5 text-[9px] text-(--cd-text-faint) ${className ?? ''}`}
    >
      {parent === null ? null : (
        <span className="max-w-full break-words">
          <span aria-hidden="true">↳ </span>
          from {parent.title}
        </span>
      )}
      {others.length === 0 ? null : (
        /* Plain wrapping text, with no tooltip and no tab stop. The list was
           a ⓘ glyph with the names in a tooltip, which this repo renders as a
           Radix Root: it opens on hover and on focus, and Radix closes it on
           `pointerdown`, so a tap revealed nothing at all. Once the names are
           visible and wrap, a focusable trigger repeating them would only add
           a non-interactive stop per card whose description equals its own
           name. The containers it does show are named rather than counted,
           because "also in 3 others" leaves the coach to go and find them. */
        <span className="max-w-full break-words rounded-full bg-(--cd-gray)/12 px-[5px] font-bold text-(--cd-gray)">
          {alsoIn}
        </span>
      )}
    </div>
  )
}

export interface ScheduleCardActions {
  onRemove: (cardId: string) => void
  onConvertToAiMock: (cardId: string) => void
  onRevertAiMock: (cardId: string) => void
  onOpenLink: (cardId: string) => void
  onOpenReview: () => void
  /**
   * Opens the card-details modal (LG-360 §1). Present on both the persisted and
   * the mock board — the mock slice simply supplies no write callbacks, so the
   * modal opens read-only there.
   */
  onOpenDetails?: (cardId: string) => void
  targetCompanies?: Array<{ id: string; name: string }>
  onSetTargetCompany?: (cardId: string, targetCompanyId: string | null) => void
  /**
   * The inline source switcher's write (LG-362 §2) — reassigns the card to
   * the clicked source. Absent on the mock board, where the mark stays
   * static.
   */
  onSwitchSource?: (cardId: string, sourceId: string) => Promise<unknown>
}

export interface ScheduleCardProps extends ScheduleCardActions {
  card: TrayCard
  readOnly?: boolean
}

export function ScheduleCard(props: ScheduleCardProps): ReactNode {
  const {
    card,
    readOnly = false,
    onRemove,
    onConvertToAiMock,
    onRevertAiMock,
    onOpenLink,
    onOpenReview,
    onOpenDetails,
    targetCompanies,
    onSetTargetCompany,
    onSwitchSource,
  } = props

  const lifecycleMutable =
    card.status === undefined ||
    card.status === 'planned' ||
    card.status === 'in_progress'
  const canMutate = !readOnly && lifecycleMutable && !card.done && !card.fixed
  const canConvert =
    canMutate && !card.aiMock && CONVERTIBLE_TYPES.has(card.type)
  const canRevert = canMutate && Boolean(card.aiMock)
  const cancelled = card.status === 'cancelled'
  // Two staged faces of one problem read identically without the source, so it
  // rides on the name rather than only on the mark.
  const detailsLabel = card.sourceDisplayName
    ? `${card.title} · ${card.sourceDisplayName}`
    : card.title
  // A fixed card is a synced calendar event, not an assignment: it has no
  // notes, no completion mode and no catalog item behind it, so a details
  // modal would open on an empty shell.
  const openDetails = card.fixed ? undefined : onOpenDetails
  // The switcher owns its degrade rule — with no write it renders the static
  // mark itself. This only decides whether a write exists: absent on the
  // mock board, and gated on `canMutate` like every other write (a published
  // week and a closed card keep only read affordances).
  const pickSource =
    onSwitchSource && canMutate
      ? (sourceId: string) => onSwitchSource(card.id, sourceId)
      : undefined

  return (
    <article
      aria-label={card.title}
      className="mb-1.5 rounded-[7px] border border-(--cd-border-2) bg-(--cd-inset) px-2 py-1.5"
      style={{
        borderLeft: `3px solid ${TYPE_COLOR[card.type]}`,
        opacity: cancelled ? 0.45 : card.done ? 0.55 : card.fixed ? 0.85 : 1,
      }}
    >
      {/* The pill sits on its own right-aligned row rather than as a sibling
          of the title column. A day column is ~130px at the grid's 920px
          floor, and a `shrink-0 whitespace-nowrap` pill on the title row would
          leave the title barely 20px — one word per line. Fixed cards never
          render the action buttons (`canMutate` is false when `card.fixed`),
          so this row is theirs alone. */}
      {card.fixed ? (
        <div className="mb-1 flex justify-end">
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                tabIndex={0}
                className="whitespace-nowrap rounded-full bg-(--cd-gray)/12 px-[7px] text-[9px] font-bold text-(--cd-gray)"
              >
                📅 immovable
              </span>
            </TooltipTrigger>
            <TooltipContent>
              Synced from the calendar — can&apos;t be moved or removed here.
            </TooltipContent>
          </Tooltip>
        </div>
      ) : null}

      <div className="flex items-start gap-1">
        <div className="min-w-0 flex-1">
          {/* The title block is the details affordance where one exists. A
              drag needs 6px of pointer travel to arm (the board's
              PointerSensor activation distance), so a plain click reaches this
              without ever starting one. The tray's equivalent block is already
              its placement control, so that surface gets a separate button
              instead — see `staging-tray.tsx`. */}
          {openDetails ? (
            <div className="flex items-start gap-1">
              {/* A sibling of the details button, not a child: the switcher's
                  marks are buttons themselves, and buttons cannot nest. The
                  source still rides on `detailsLabel`, or the two staged faces
                  of one problem announce identically. */}
              <InlineSourceSwitcher
                sources={card.sources ?? []}
                currentSourceId={card.sourceId ?? null}
                fallbackIcon={card.sourceIcon ?? null}
                onPick={pickSource}
              />
              <button
                aria-label={`Open details for ${detailsLabel}`}
                className="block min-w-0 flex-1 cursor-pointer text-left"
                onClick={() => openDetails(card.id)}
                type="button"
              >
                <span
                  className="block text-[11px] font-semibold text-(--cd-text)"
                  style={{
                    textDecoration:
                      card.done || cancelled ? 'line-through' : 'none',
                  }}
                >
                  {card.title}
                </span>
                <span className="block text-[9.5px] text-(--cd-text-faint)">
                  {card.meta}
                </span>
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-1">
                {/* `fallbackLabel`, unlike the details branch above: nothing
                    else in this branch names the source, so the degraded
                    static mark must carry it itself. */}
                <InlineSourceSwitcher
                  sources={card.sources ?? []}
                  currentSourceId={card.sourceId ?? null}
                  fallbackIcon={card.sourceIcon ?? null}
                  fallbackLabel={card.sourceDisplayName}
                  onPick={pickSource}
                />
                <div
                  className="min-w-0 text-[11px] font-semibold text-(--cd-text)"
                  style={{
                    textDecoration:
                      card.done || cancelled ? 'line-through' : 'none',
                  }}
                >
                  {card.title}
                </div>
              </div>
              <div className="text-[9.5px] text-(--cd-text-faint)">
                {card.meta}
              </div>
            </>
          )}
          <CardParentage card={card} />
          {cancelled ? (
            <span className="mt-0.5 inline-flex rounded-full bg-(--cd-red)/12 px-1.5 text-[9px] font-bold text-(--cd-red)">
              Cancelled · hidden from client
            </span>
          ) : null}

          {/* Guard the sink too: legacy rows / API-written links never passed
              the form's http/https validation (LG-173). */}
          {card.link && isAbsoluteUrl(card.link) ? (
            <a
              className="block text-[9px] font-semibold text-(--cd-green) hover:text-(--cd-green-hover)"
              href={card.link}
              rel="noreferrer"
              target="_blank"
            >
              🔗 open link
            </a>
          ) : null}

          {canMutate && targetCompanies?.length && onSetTargetCompany ? (
            <select
              aria-label={`Target company for ${card.title}`}
              value={card.targetCompanyId ?? ''}
              onChange={(event) =>
                onSetTargetCompany(card.id, event.currentTarget.value || null)
              }
              className="mt-1 block max-w-full rounded border border-(--cd-border) bg-(--cd-bg) px-1 py-0.5 text-[9px] text-(--cd-text-muted)"
            >
              <option value="">No target company</option>
              {targetCompanies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          ) : null}

          {card.repeat ? (
            <span className="mt-[3px] inline-flex items-center rounded-full bg-(--cd-amber)/12 px-[7px] text-[9px] font-bold text-(--cd-amber)">
              ↻ {card.repeat} ·{' '}
              <button
                aria-label={`View prior review for ${card.title}`}
                className="ml-1 cursor-pointer border-0 bg-transparent p-0 text-[9px] font-bold text-(--cd-amber) hover:text-(--cd-amber-hover)"
                onClick={onOpenReview}
                type="button"
              >
                view
              </button>
            </span>
          ) : null}

          {card.aiMock && !card.needsLink ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  tabIndex={0}
                  className="mt-[3px] inline-block whitespace-nowrap rounded-full bg-(--cd-green)/10 px-[7px] text-[9px] font-bold text-(--cd-green)"
                >
                  🤖 AI mock · title hidden
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {
                  "The client sees only 'AI mock interview' — never the question title."
                }
              </TooltipContent>
            </Tooltip>
          ) : null}

          {card.needsLink ? (
            readOnly ? (
              <span className="mt-[3px] block w-fit whitespace-nowrap rounded-md border border-dashed border-(--cd-amber)/40 bg-(--cd-amber)/10 px-[7px] py-0.5 text-[9px] font-bold text-(--cd-amber)">
                ⚠ link a question…
              </span>
            ) : (
              <button
                aria-label={`Link a question to ${card.title}`}
                className="mt-[3px] block cursor-pointer whitespace-nowrap rounded-md border border-dashed border-(--cd-amber)/40 bg-(--cd-amber)/10 px-[7px] py-0.5 text-[9px] font-bold text-(--cd-amber)"
                onClick={() => onOpenLink(card.id)}
                type="button"
              >
                ⚠ link a question…
              </button>
            )
          ) : null}
        </div>

        {canConvert ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                aria-label={`Convert ${card.title} to AI mock`}
                className="shrink-0 cursor-pointer border-0 bg-transparent p-0 text-[9.5px] opacity-65 hover:opacity-100"
                onClick={() => onConvertToAiMock(card.id)}
                type="button"
              >
                🤖
              </button>
            </TooltipTrigger>
            <TooltipContent>
              {
                'Convert to an AI mock — same question, but the client won’t see the title, only "AI mock interview"'
              }
            </TooltipContent>
          </Tooltip>
        ) : null}

        {canRevert ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                aria-label={`Revert ${card.title} to normal assignment`}
                className="shrink-0 cursor-pointer border-0 bg-transparent p-0 text-[9.5px] opacity-65 hover:opacity-100"
                onClick={() => onRevertAiMock(card.id)}
                type="button"
              >
                ↩
              </button>
            </TooltipTrigger>
            <TooltipContent>
              Revert to a normal assignment — the client will see the title
              again
            </TooltipContent>
          </Tooltip>
        ) : null}

        {canMutate ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                aria-label={`Remove ${card.title} — moves to staging tray`}
                className="shrink-0 cursor-pointer border-0 bg-transparent p-0 text-[10px] text-(--cd-text-ghost) hover:text-(--cd-red)"
                onClick={() => onRemove(card.id)}
                type="button"
              >
                ✕
              </button>
            </TooltipTrigger>
            <TooltipContent>Remove — moves to staging tray</TooltipContent>
          </Tooltip>
        ) : null}
      </div>
    </article>
  )
}
